import { type Dispatch } from "@reduxjs/toolkit";
import { type FC, useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "../../../../../Application/globaltypes/redux";
import { usePerformanceBreadcrumbs } from "../../../../Common/Hooks/usePerformanceBreadcrumbs";
import { reset } from "../../state/slices/emailPerformanceSlice";
import EmailPerformanceBody from "./EmailPerformanceBody";

import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { bindAction } from "interfaces";
import ReportingExport from "../../../../../../components/reportingExport/ReportingExport";
import ReportingFilter from "../../../../../../components/reportingFilter/ReportingFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";

import "../../../../Common/utils/performanceSCSSUtils.scss";
import "./emailPerformance.scss";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { emailsStateSelector } from "../../state/selector/emailCommonSelector";

export type Props = PropsFromRedux & {
  emailId: number;
  emailTitle: string;
};

export const EmailPerformance: FC<Props> = ({
  accountId,
  accountType,
  emailId,
  emailTitle,
  resetState,
  isExportEnabled,
  publisherId,
}) => {
  const includeAccountsDropdown = useAccountFilterShouldShow(accountId, accountType, true);
  const [filter, setFilter] = usePerformanceFilter(
    {
      includeDistinct: true,
      supportsSPType: true,
      includeAccountsDropdown,
      accountId,
    },
    publisherId,
  );

  const reportEnabled = useFeatureFlag(FeatureFlags.EmailReport);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs("All Sends");

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className="performanceRoot">
      <header className="emailPerformanceHeader">
        {hasBreadcrumbsToDisplay ? (
          <div className="breadCrumbs">{domElements}</div>
        ) : (
          <h2 className="emailTitle">Summary</h2>
        )}
        <div className="emailActions">
          <ReportingFilter
            currentFilter={filter}
            callback={setFilter}
            includeAccounts={false}
            includeAccountsDropdown={includeAccountsDropdown}
            ignoreInCountAndHide={["type"]}
            includeDistinct
            supportsSPType
            publisherId={publisherId}
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      </header>
      <div className={"performanceRoot"}>
        <EmailPerformanceBody
          emailId={emailId}
          emailTitle={emailTitle}
          registerBreadcrumb={registerBreadcrumb}
          // @ts-ignore
          dateFilter={filter}
        />
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    isExportEnabled: selectIsExportActive(state),
    publisherId: emailsStateSelector(state).emailDetailsReducer.email.ownerId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    resetState: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EmailPerformance);
export default ConnectedComponent;
